import { Children, isValidElement } from "react";
import type { ComponentPropsWithoutRef, ReactNode } from "react";

// Relative imports on purpose: vitest.config.ts does not alias "@".
import { headingId } from "../../lib/blog/format";
import { BlogAnchor } from "./BlogAnchor";
import { BlogLink } from "./BlogLink";
import { BlogStoreCard } from "./BlogStoreCard";
import { BuyerQuote } from "./BuyerQuote";
import { Callout } from "./Callout";
import { CategoryLink } from "./CategoryLink";
import { DataBlock } from "./DataBlock";

/** Flattens heading children to plain text, so the h2 id matches the TOC's headingId(raw line). */
const plainText = (children: ReactNode): string =>
  Children.toArray(children)
    .map((child) => {
      if (typeof child === "string" || typeof child === "number") return String(child);
      if (isValidElement<{ children?: ReactNode }>(child)) return plainText(child.props.children);
      return "";
    })
    .join("");

/**
 * The runtime allowlist: the ONLY component map passed to every MDX post body (D-07).
 *
 * Capitalized keys must equal ALLOWED_COMPONENTS in frontend/scripts/blog/blogConfig.mjs (the
 * build validator); mdxComponents.test.tsx enforces it, so adding a component means adding it in
 * both places. There is deliberately no `img` override: images are not allowed in this phase and
 * the validator rejects them.
 */
export const mdxComponents = {
  h2: ({ children, ...props }: ComponentPropsWithoutRef<"h2">) => (
    <h2
      {...props}
      id={headingId(plainText(children))}
      className="text-xl leading-normal font-bold text-gray-900 mt-8 mb-4 border-r-4 border-warmOrange-500 pr-3 scroll-mt-24"
    >
      {children}
    </h2>
  ),
  h3: (props: ComponentPropsWithoutRef<"h3">) => (
    <h3 {...props} className="text-base font-bold text-gray-900 mt-6 mb-2" />
  ),
  p: (props: ComponentPropsWithoutRef<"p">) => (
    <p {...props} className="text-base leading-[1.8] text-gray-700 mb-4" />
  ),
  ul: (props: ComponentPropsWithoutRef<"ul">) => (
    <ul {...props} className="list-disc pr-6 mb-4 space-y-2 text-gray-700 leading-[1.8] [&_ul]:mt-2 [&_ol]:mt-2" />
  ),
  ol: (props: ComponentPropsWithoutRef<"ol">) => (
    <ol {...props} className="list-decimal pr-6 mb-4 space-y-2 text-gray-700 leading-[1.8] [&_ul]:mt-2 [&_ol]:mt-2" />
  ),
  li: (props: ComponentPropsWithoutRef<"li">) => <li {...props} />,
  a: ({ href, title, children }: ComponentPropsWithoutRef<"a">) => (
    <BlogAnchor href={href} title={title}>
      {children}
    </BlogAnchor>
  ),
  strong: (props: ComponentPropsWithoutRef<"strong">) => (
    <strong {...props} className="font-bold text-gray-900" />
  ),
  table: (props: ComponentPropsWithoutRef<"table">) => (
    <div className="overflow-x-auto my-8">
      <table {...props} className="w-full text-sm border-collapse" />
    </div>
  ),
  th: (props: ComponentPropsWithoutRef<"th">) => (
    <th {...props} className="text-start font-bold bg-gray-50 border-b border-gray-200 px-4 py-2" />
  ),
  td: (props: ComponentPropsWithoutRef<"td">) => (
    <td {...props} className="text-start border-b border-gray-100 px-4 py-2 align-top" />
  ),
  blockquote: (props: ComponentPropsWithoutRef<"blockquote">) => (
    <blockquote {...props} className="border-r-4 border-gray-300 pr-4 my-6 text-gray-600" />
  ),
  hr: (props: ComponentPropsWithoutRef<"hr">) => <hr {...props} className="my-8 border-gray-200" />,
  DataBlock,
  StoreCard: BlogStoreCard,
  BuyerQuote,
  CategoryLink,
  BlogLink,
  Callout,
};

export default mdxComponents;
